import {FormControl, FormGroup, Validators} from '@angular/forms';

import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ServerResponse,
    ServerResponseData,
    PaymentMethodsEnum,
    HttpStatusCodesEnum,
    PaymentProvidersEnum,
    PaymentsService,
    LinkedAccountModel,
    TradingAccountModel,
    WithdrawalInputModel,
    InternalTransferInputModel,
    CashAccountDetailedReportModel,
    TradingAccountDetailedReportModel,
    IAccountCashBalancesResultItem, IAccountCashBalance, AccountSummary
} from '@spa/api';

import {InArrayValidator, InvalidAmountKey} from '@spa/validators';

import {Helper, AmountControl, AutocompleteControl, BaseControl, SelectControl} from '../../../../shared/classes';

import {
    WithdrawalComponentControls,
    WithdrawalAmountSelector,
    WithdrawalAccountToSelector,
    WithdrawalAccountFromSelector,
    WithdrawalLinkedAccountToSelector, WithdrawalCurrencySelector, BankAccountWithdrawalAvailableCurrencies
} from '../../../../../assets/descriptors/panel-accounts';

import {WithdrawalFormErrorStatesEnum} from '../../enums/error-states';
import {AutocompleteOptionInterface, SelectOptionInterface} from 'appShared/components';

export class WithdrawalForm {
    private readonly paymentsService: PaymentsService;
    private readonly tradingAccounts: TradingAccountModel[];
    private readonly linkedAccounts: LinkedAccountModel[];
    private paymentMethod: number;
    private readonly account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;
    private paymentMethodsEnum: typeof PaymentMethodsEnum;
    private paymentProvidersEnum: typeof PaymentProvidersEnum;
    public balanceData: IAccountCashBalancesResultItem;
    public form: FormGroup;
    public controls: BaseControl[];

    constructor(
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        linkedAccounts: LinkedAccountModel[],
        tradingAccounts: TradingAccountModel[],
        paymentsService: PaymentsService,
        balanceData: IAccountCashBalancesResultItem
    ) {
        this.account = account;
        this.linkedAccounts = linkedAccounts;
        this.tradingAccounts = tradingAccounts;
        this.paymentsService = paymentsService;
        this.paymentMethodsEnum = PaymentMethodsEnum;
        this.paymentProvidersEnum = PaymentProvidersEnum;
        this.balanceData = balanceData;
    }

    private initControl(control: BaseControl): BaseControl {
        switch (control.name) {
            case WithdrawalAccountToSelector:
                const accountIds = [];

                (control as AutocompleteControl).options = this.tradingAccounts
                    .filter((account: TradingAccountModel) => account.id !== this.account.accountId)
                    .map((account: TradingAccountModel) => {
                        accountIds.push(account.id);

                        return {
                            label: account.accountReferenceId,
                            value: account.id
                        };
                    });

                control.extendValidators(InArrayValidator(accountIds));
                break;

            case WithdrawalCurrencySelector:
                if (this.balanceData && this.balanceData.cashBalances.length > 0) {
                    let currencyOptions = this.balanceData.cashBalances.map((x: IAccountCashBalance): SelectOptionInterface => {
                        return {
                            label: x.currency.toUpperCase(),
                            value: x.currency.toUpperCase(),
                        };
                    });
                    if (this.paymentMethod === this.paymentMethodsEnum.BankTransfer){
                        currencyOptions = currencyOptions.filter(option =>
                            BankAccountWithdrawalAvailableCurrencies.includes(option.value as string));
                    }

                    (control as SelectControl).options.push(...currencyOptions);
                    control.extendValidators(InArrayValidator(currencyOptions.map(x => x.label)));
                    control.value = this.account.currencyCode;
                }

                break;

            case WithdrawalLinkedAccountToSelector:
                const linkedAccountIds = this.linkedAccounts.map((linkedAccount: LinkedAccountModel) => linkedAccount.id);

                const options =
                    this.linkedAccounts.map((linkedAccount: LinkedAccountModel) => {
                        return {
                            label: linkedAccount.accountInfo.bankName,
                            value: linkedAccount.id
                        };
                    });
                (control as AutocompleteControl).options = options;
                control.value = options.length === 1 ?  options[0].value : null ;
                control.extendValidators(InArrayValidator(linkedAccountIds));
                break;
            case WithdrawalAccountFromSelector:
                control.value = this.account.accountReferenceId;
                break;
        }

        return control;
    }

    private createBankWithdrawal(amount: string): ServerResponse<boolean> {
        const linkedAccountId = this.form.controls[WithdrawalLinkedAccountToSelector].value;
        const currency = this.form.controls[WithdrawalCurrencySelector].value;

        const linkedAccounts = this.linkedAccounts.filter((account: LinkedAccountModel) => {
            return account.id === linkedAccountId;
        });

        const model = new WithdrawalInputModel(
            PaymentProvidersEnum.UnknownBank,
            PaymentMethodsEnum.BankTransfer,
            this.account.accountId,
            amount,
            (linkedAccounts.length !== 0) ? linkedAccounts[0].accountInfo : null,
            currency
        );

        return this.paymentsService.registerWithdrawal(model).pipe(
            map((response: ServerResponseData<string>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<string>(
                        response,
                        WithdrawalFormErrorStatesEnum.RegisterBankWithdrawalFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    accountBalanceChanged(value: number): void {
        this.controls.map((control: BaseControl) => {
            switch (control.name) {
                case WithdrawalAmountSelector:
                    control.resetErrors();
                    control.resetValue();

                    this.form.get(WithdrawalAmountSelector).reset();
                    break;
                case WithdrawalCurrencySelector:
                    this.reInitCurrencyControl();
                    break;
            }
        });
    }

    public reInitCurrencyControl(): void {
        let options: SelectOptionInterface[] = [];
        const control = this.controls[3] as SelectControl;
        const formControl = this.form.get(WithdrawalCurrencySelector);

        control.resetValue();
        formControl.reset();
        options =  this.balanceData.cashBalances.map(x => {
            return {
                label: x.currency.toUpperCase(),
                value: x.currency.toUpperCase()
            };
        });
        if (this.paymentMethod === this.paymentMethodsEnum.BankTransfer){
            options = options.filter(option =>
                BankAccountWithdrawalAvailableCurrencies.includes(option.value as string));
        }
        control.options = options;
        control.disabled = options.length === 1;

        if (options.length > 0) {
            control.value = options[0].value as string;
            formControl.patchValue(options[0].value);
        }

        this.form.updateValueAndValidity();
    }

    initForm(method: number): void {
        this.form = new FormGroup({});
        this.paymentMethod = method;
        this.controls = WithdrawalComponentControls()
            .filter((control: BaseControl) => {
                if (
                    (method === PaymentMethodsEnum.InternalTransfer) &&
                    (control.name === WithdrawalLinkedAccountToSelector)
                ) {
                    return false;
                }

                return !((method === PaymentMethodsEnum.BankTransfer) &&
                    (control.name === WithdrawalAccountToSelector));
            })
            .map((control: BaseControl) => {
                control = this.initControl(control);

                this.form.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlMaxLength?: number) => { [key: string]: string; },
        method: number,
        unavailableCashMessageKey?: string,
        notEnoughMoneyMessageKey?: string
    ): boolean {
        const statuses = this.controls.map((control: BaseControl) => {
            const formControl = this.form.controls[control.name];

            if (!formControl) {
                return false;
            }

            if (formControl.invalid) {
                control.errors = Object
                    .keys(formControl.errors)
                    .map((errorKey: string) => {
                        return messageFunction(control.placeholder)[errorKey];
                    });

                return false;
            }

            const amountControlValue = this.form.controls[WithdrawalAmountSelector].value;
            const currenControlValue = this.form.controls[WithdrawalCurrencySelector].value;

            if (amountControlValue && currenControlValue) {
                const fixedAmount = parseFloat(Helper.compactString(amountControlValue));

                switch (control.name) {
                    case WithdrawalAmountSelector:
                        if (fixedAmount === 0) {
                            control.setError(messageFunction()[InvalidAmountKey]);

                            return false;
                        }
                        break;
                    case WithdrawalAccountFromSelector:
                        let availableAccountFromCash = '0.00';
                        const currencyControlValue = this.form.value[WithdrawalCurrencySelector];
                        const balanceWithCurrency = this.balanceData?.cashBalances?.find(x => x.currency === currencyControlValue);

                        if (balanceWithCurrency === null) {
                            control.setError(messageFunction()[unavailableCashMessageKey]);

                            return false;
                        }
                        availableAccountFromCash = balanceWithCurrency.netCash.toFixed(2);


                        if (fixedAmount > parseFloat(availableAccountFromCash)) {
                            control.setError(messageFunction()[notEnoughMoneyMessageKey]);

                            return false;
                        }
                        break;
                }
            }

            return true;
        });

        return statuses.indexOf(false) < 0;
    }

    createRequest(method: number): ServerResponse<boolean> {
        const amount = Helper.compactString(this.form.controls[WithdrawalAmountSelector].value);
        const currency = this.form.controls[WithdrawalCurrencySelector].value;

        switch (method) {
            case PaymentMethodsEnum.BankTransfer:
                return this.createBankWithdrawal(amount);
            case PaymentMethodsEnum.InternalTransfer:
                const model = new InternalTransferInputModel(
                    parseFloat(amount),
                    this.account.accountId,
                    this.form.controls[WithdrawalAccountToSelector].value,
                    currency
                );

                return this.paymentsService.registerInternalTransfer(model).pipe(
                    map((response: ServerResponseData<string>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<string>(
                                response,
                                WithdrawalFormErrorStatesEnum.RegisterInternalWithdrawalFailed
                            );
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        const error = Helper.errorsHelper()
            .createResponseForInvalidData(WithdrawalFormErrorStatesEnum.CanFindCorrectPaymentMethod);

        return of(error);
    }
}
